import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';

/**
 * Avertissement inline (icône + texte, token sémantique — jamais la couleur seule).
 * Utilisé quand « conserver un original » est DÉCOCHÉ : les fichiers sont modifiés
 * SANS sauvegarde `_original` (irréversible).
 */
export function InlineWarning({ text }: { text: string }): React.JSX.Element {
  return (
    <p
      role="note"
      className="inline-flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-fg"
    >
      <ExclamationTriangleIcon
        className="mt-0.5 h-5 w-5 shrink-0 text-warning-text"
        aria-hidden="true"
      />
      <span>{text}</span>
    </p>
  );
}

/**
 * Contrôles PARTAGÉS entre les deux outils EXIF (« Nettoyage EXIF » = effacement et
 * « Publier des tags vers EXIF » = publication) — extraits pour éviter la duplication
 * après le SPLIT en deux pages.
 */

/** Sélecteur de portée par dossier (optionnel) — utilise les facettes réelles. */
export function FolderScope({
  idPrefix,
  value,
  options,
  onChange,
}: {
  idPrefix: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const id = `${idPrefix}-folder`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {t('exif.folderScope')}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <option value="">{t('exif.folderAll')}</option>
        {options
          .filter((p) => p !== '')
          .map((path) => (
            <option key={path} value={path}>
              {path}
            </option>
          ))}
      </select>
    </div>
  );
}

/** Bascule accessible (case à cocher stylée) avec libellé + aide. */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint: string;
}): React.JSX.Element {
  return (
    <label className="flex items-start gap-2 text-sm text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      />
      <span className="flex flex-col">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted">{hint}</span>
      </span>
    </label>
  );
}

/**
 * Statistique de divergence (icône + texte, jamais la couleur seule). Si `onSelect`
 * est fourni ET la valeur > 0, la tuile devient un bouton de lien profond (galerie
 * filtrée) ; sinon elle reste informative.
 */
export function Stat({
  label,
  value,
  icon,
  onSelect,
  actionLabel,
}: {
  label: string;
  value: number;
  icon?: 'sync' | 'warn';
  onSelect?: () => void;
  actionLabel?: string;
}): React.JSX.Element {
  const { locale } = useI18n();
  const inner = (
    <>
      <span className="flex items-center gap-1 text-xs font-medium text-muted">
        {icon === 'sync' && <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
        {icon === 'warn' && (
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
        )}
        {icon === undefined && (
          <DocumentDuplicateIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        {label}
      </span>
      <span className="text-lg font-semibold tabular-nums text-fg">
        {value.toLocaleString(locale)}
      </span>
    </>
  );

  if (onSelect && value > 0) {
    return (
      <button
        type="button"
        onClick={onSelect}
        title={actionLabel}
        aria-label={actionLabel}
        className="flex flex-col gap-0.5 rounded-lg border border-border bg-surface-2 p-3 text-left hover:border-brand hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {inner}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-surface-2 p-3">
      {inner}
    </div>
  );
}
